import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/axios";
import {
  getListJenisBerkas,
  // getListJenjangPendidikan,
} from "@/services/getData";
import {
  JenisBerkas,
  JenjangPendidikan,
  Periode,
  SysBerkas,
  SysProgramStudi,
} from "@/types";
import { Label } from "@/components/ui/label";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useSearchParams } from "react-router-dom";
import { RiDeleteBack2Line } from "react-icons/ri";
// import { MultiSelect } from "@/components/ui/multi-select";

const postData = async (newData: SysBerkas) => {
  const response = await api.post("/setting/sys-berkas/store", newData);
  return response.data;
};

const patchData = async (newData: SysBerkas) => {
  const response = await api.patch("/setting/sys-berkas/update", newData);
  return response.data;
};

const deleteData = async (deletedData: { id_sys_berkas: number }) => {
  const response = await api({
    url: "/setting/sys-berkas/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

const listMaxFileSize = [256, 512, 1024, 2048, 5120];
const listExtension = [
  "pdf", // PDF files
  "doc", // Microsoft Word (older versions)
  "docx", // Microsoft Word (modern versions)
  "xls", // Microsoft Excel (older versions)
  "xlsx", // Microsoft Excel (modern versions)
  "jpeg", // JPEG image
  "jpg", // JPG image (alternative extension)
  "png", // PNG image
  "gif", // GIF image
];

export default function FormSysBerkas({
  method,
  selectedRow,
  selectedIdSysBerkas,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: Periode | null;
  selectedIdSysBerkas?: number;
  closeModal: () => void;
}) {
  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const [searchParams] = useSearchParams();
  const id_sys_periode_params = searchParams.get("id");

  const selectedSysBerkas = selectedRow?.sys_berkas?.find(
    (item: SysBerkas) => item.id_sys_berkas == selectedIdSysBerkas,
  );

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [form, setForm] = useState<SysBerkas>({
    id_sys_berkas: selectedSysBerkas?.id_sys_berkas || 0,
    id_ref_jenis_berkas: selectedSysBerkas?.id_ref_jenis_berkas || "",
    id_sys_periode: Number(selectedRow?.id_sys_periode),
    is_wajib: selectedSysBerkas?.is_wajib ?? true,
    is_syarat_prodi: selectedSysBerkas?.is_syarat_prodi ?? false,
    list_prodi: selectedSysBerkas?.list_prodi || [],
    url_template_berkas: selectedSysBerkas?.url_template_berkas || "",
    keterangan_berkas: selectedSysBerkas?.keterangan_berkas || "",
    is_active: selectedSysBerkas?.is_active ?? true,
    max_upload: selectedSysBerkas?.max_upload || 0,
    extension_allowed: selectedSysBerkas?.extension_allowed || [],
  });

  const [searchQueryProdi, setSearchQueryProdi] = useState("");
  // const [selectedJenjangs, setSelectedJenjangs] = useState<string[]>([]);

  const jenjangBuka = selectedRow?.jenjang_buka
    ? selectedRow?.jenjang_buka.map((item) => item.id_ref_jenjang_pendidikan)
    : [];

  const { data: dataJenisBerkas, isPending: isPendingJenisBerkas } = useQuery({
    queryKey: ["listJenisBerkas"],
    queryFn: getListJenisBerkas,
  });

  // const { data: dataJenjangPendidikan } = useQuery({
  //   queryKey: ["listJenjangPendidikan"],
  //   queryFn: getListJenjangPendidikan,
  //   select: (data) =>
  //     data?.map((jenjangPendidikan: JenjangPendidikan) => ({
  //       label: jenjangPendidikan.nama_jenjang_pendidikan,
  //       value: `${jenjangPendidikan.id_ref_jenjang_pendidikan}`,
  //     })),
  // });

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPeriode"] });
      queryClient.invalidateQueries({
        queryKey: ["periode", id_sys_periode_params],
      });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  const filteredJenisBerkas = dataJenisBerkas?.filter(
    (jenisBerkas: JenisBerkas) =>
      jenisBerkas.untuk_daftar_jenjang?.some((jenjang: JenjangPendidikan) =>
        jenjangBuka.includes(jenjang.id_ref_jenjang_pendidikan),
      ),
  );

  const filteredProdi = selectedRow?.sys_prodi
    ? selectedRow?.sys_prodi.filter(
        (prodi) =>
          prodi.unitkerja?.nama &&
          prodi.unitkerja?.nama
            .toLowerCase()
            .includes(searchQueryProdi.toLowerCase()),
      )
    : [];

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Setting Berkas"
          description={`Apakah anda yakin akan menghapus data?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit}>
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data List Berkas" : "Update Data List Berkas"}
      />
      <div
        className={`grid gap-5 ${
          form.is_syarat_prodi ? "grid-cols-2" : "grid-cols-1"
        }`}
      >
        <section className="flex flex-col gap-y-3">
          {/* <div>
            <Label>Jenjang Pendidikan</Label>
            <MultiSelect
              options={dataJenjangPendidikan || []}
              onValueChange={(value) => {
                setSelectedJenjangs(value);
                setForm((prev) => ({ ...prev, id_ref_jenis_berkas: "" }));
              }}
              defaultValue={selectedJenjangs}
              placeholder="Pilih Jenjang"
              variant="inverted"
              animation={2}
              maxCount={3}
            />
          </div> */}

          <div>
            <Label>Jenis Berkas</Label>
            <Select
              value={form.id_ref_jenis_berkas}
              onValueChange={(value) => {
                setForm((prev) => ({
                  ...prev,
                  id_ref_jenis_berkas: value,
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Jenis Berkas"}>
                  {form.id_ref_jenis_berkas &&
                    filteredJenisBerkas?.find(
                      (jenisBerkas: JenisBerkas) =>
                        jenisBerkas.id_ref_jenis_berkas ==
                        form.id_ref_jenis_berkas,
                    )?.nama_jenis_berkas}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {isPendingJenisBerkas ? (
                  "Loading..."
                ) : filteredJenisBerkas?.length > 0 ? (
                  filteredJenisBerkas?.map((jenisBerkas: JenisBerkas) => (
                    <SelectItem
                      key={jenisBerkas.id_ref_jenis_berkas}
                      value={`${jenisBerkas.id_ref_jenis_berkas}`}
                    >
                      {jenisBerkas.nama_jenis_berkas}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-5">
                    Berkas untuk jenjang yang dipilh tidak ditemukan.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Keterangan Berkas</Label>
            <Input
              placeholder="Ketik keterangan berkas..."
              value={form.keterangan_berkas}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  keterangan_berkas: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Url Template Berkas</Label>
            <Input
              placeholder="Ketik url template berkas..."
              value={form.url_template_berkas}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  url_template_berkas: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Max File Size</Label>
            <div className="mt-1 flex gap-3">
              {listMaxFileSize.map((size) => {
                return (
                  <div key={size} className="flex items-center gap-3">
                    <Checkbox
                      checked={form.max_upload === size}
                      onCheckedChange={() =>
                        setForm((prev) => ({ ...prev, max_upload: size }))
                      }
                    />
                    <Label>{size} KB</Label>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mb-3">
            <Label>Extension Allowed</Label>
            <div className="mt-1 flex gap-3">
              {listExtension.map((extension) => {
                const isChecked = form.extension_allowed?.includes(extension);

                return (
                  <div key={extension} className="flex items-center gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          extension_allowed: isChecked
                            ? prev.extension_allowed?.filter(
                                (ext) => ext !== extension,
                              ) || []
                            : [...(prev.extension_allowed || []), extension],
                        }))
                      }
                    />
                    <Label>{extension}</Label>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex gap-3">
              <Switch
                id="is_wajib"
                checked={form.is_wajib}
                onCheckedChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    is_wajib: !prev.is_wajib,
                  }))
                }
              />
              <Label htmlFor="is_wajib">Wajib (tidak/ya)</Label>
            </div>
          </div>

          <div className="flex gap-3">
            <Switch
              id="is_syarat_prodi"
              checked={form.is_syarat_prodi}
              onCheckedChange={() =>
                setForm((prev) => ({
                  ...prev,
                  is_syarat_prodi: !prev.is_syarat_prodi,
                }))
              }
            />
            <Label htmlFor="is_syarat_prodi">Syarat Prodi (tidak/ya)</Label>
          </div>

          <div>
            <div className="flex gap-3">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    is_active: !prev.is_active,
                  }))
                }
              />
              <Label htmlFor="is_active">Status (nonaktif/aktif)</Label>
            </div>
          </div>

          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              onClick={() => closeModal()}
            >
              Batal
            </Button>
            <Button className="flex items-center gap-3" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Simpan"
              )}
            </Button>
          </div>

          {selectedRow?.sys_berkas && (
            <div className="mt-3 border rounded-xl p-3">
              <h1 className="font-semibold">Berkas yang sudah di tambahkan.</h1>
              <ul>
                {selectedRow?.sys_berkas.map((item, index) => {
                  return (
                    <li key={index}>
                      - {item.jenis_berkas?.nama_jenis_berkas}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>

        {form.is_syarat_prodi ? (
          <section>
            <Label>Pilih Prodi untuk syarat berkas</Label>
            <Input
              placeholder="Search prodi..."
              value={searchQueryProdi}
              onChange={(event) => setSearchQueryProdi(event.target.value)}
            />
            <div className="mt-3 flex gap-3">
              <Checkbox
                checked={isCheckAll}
                onCheckedChange={() => {
                  setIsCheckAll((prev) => !prev);

                  setForm((prev) => ({
                    ...prev,
                    list_prodi: isCheckAll
                      ? []
                      : selectedRow?.sys_prodi?.map(
                          (prodi: SysProgramStudi) => ({
                            id_sys_program_studi: prodi?.id_sys_program_studi,
                            unitkerja: {
                              uk_id: prodi.uk_id || "",
                              nama: prodi.unitkerja?.nama,
                            },
                          }),
                        ) || [],
                  }));
                }}
              />

              <Label className="text-primary">Check All</Label>
            </div>

            <div className="grid grid-cols-3 gap-5">
              {filteredProdi?.map((prodi) => {
                const isChecked = form.list_prodi?.some(
                  (p) => p.unitkerja?.uk_id === prodi.uk_id,
                );

                return (
                  <div key={prodi.uk_id} className="flex items-center gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => {
                        if (isChecked) {
                          setForm((prev) => ({
                            ...prev,
                            list_prodi: prev.list_prodi.filter(
                              (p) => p.unitkerja?.uk_id !== prodi.uk_id,
                            ),
                          }));
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            list_prodi: [
                              ...prev.list_prodi,
                              {
                                id_sys_program_studi:
                                  prodi?.id_sys_program_studi,
                                unitkerja: {
                                  uk_id: prodi.uk_id || "",
                                  nama: prodi.unitkerja?.nama,
                                },
                              },
                            ],
                          }));
                        }
                      }}
                    />
                    <Label>{prodi?.unitkerja?.nama}</Label>
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          ""
        )}
      </div>
    </form>
  );
}
